import { getLogger } from '@trimble/logging';
import { LRUCache } from 'lru-cache';
import type { GeoJsonCollection } from './features-buffer-geom-translator';
import {
  getOptimalConcurrency,
  promiseAllWithConcurrency,
} from './concurrency.utils';
import {
  createFilterClause,
  HttpStatus,
  validateGeoJsonResponse,
} from './features-tile-fetcher.utils';
import { DEFAULT_FEATURES_SERVICE_URLS } from './features-service.config';
import { featuresLoadMetrics, logFeaturesTileDebug } from './monitoring-tools';

// ============================================================================
// Types
// ============================================================================

export interface FeatureTileFetcherParams {
  projectId: string;
  /** Visible layer IDs, or a getter resolved at request time. Empty skips all fetches. */
  layerIds: readonly string[] | (() => readonly string[]);
  /** When provided, visible job IDs or a getter. Empty skips all fetches. */
  jobIds?: readonly string[] | (() => readonly string[]);
  authToken(): string | null;
  baseUrl?: string;
  tileSize?: number;
  fetchMode?: 'combined' | 'per-layer';
  /**
   * Optional CloudFront cache-busting token appended as the `timestamp` query
   * param. Accepts a static string or a getter resolved at request time, so a
   * caller can rotate it after a write to bypass a stale CDN edge entry for the
   * same tile URL. When omitted (or the getter returns undefined) no timestamp
   * param is added.
   */
  timestamp?: string | (() => string | undefined);
  loadMetrics?: boolean;
  /**
   * Per-layer mode only. Max `(tile, layer, job)` responses kept in memory so a
   * tile refetch (e.g. after showing a new job) only hits the network for
   * slices not fetched before. Defaults to 4096; 0 disables.
   */
  sliceCacheSize?: number;
  /**
   * Per-layer mode only. Called with the job ids that currently have network
   * requests in flight, whenever that set changes. Cache hits never count.
   */
  onPendingJobIdsChange?(jobIds: ReadonlySet<string>): void;
}

export interface TileFetchResult {
  data: GeoJsonCollection | null;
  status: number;
}

interface TileFetchContext {
  params: FeatureTileFetcherParams;
  layerIds: readonly string[];
  x: number;
  y: number;
  z: number;
  abortController: AbortController;
  baseUrl: string;
  tileSize: string;
  timestamp: string;
  token: string;
  jobIds: readonly string[];
  logger: ReturnType<typeof getLogger>;
  sliceCache: LRUCache<string, TileFetchResult> | undefined;
  pendingJobs: PendingJobTracker;
}

class PendingJobTracker {
  private readonly counts = new Map<string, number>();

  constructor(
    private readonly onChange?: (jobIds: ReadonlySet<string>) => void
  ) {}

  public begin(jobId: string | undefined): void {
    if (jobId === undefined || !this.onChange) {
      return;
    }
    const count = this.counts.get(jobId) ?? 0;
    this.counts.set(jobId, count + 1);
    if (count === 0) {
      this.notify();
    }
  }

  public end(jobId: string | undefined): void {
    if (jobId === undefined || !this.onChange) {
      return;
    }
    const count = this.counts.get(jobId) ?? 0;
    if (count <= 1) {
      this.counts.delete(jobId);
      this.notify();
    } else {
      this.counts.set(jobId, count - 1);
    }
  }

  private notify(): void {
    this.onChange?.(new Set(this.counts.keys()));
  }
}

const buildSliceCacheKey = (
  context: Pick<TileFetchContext, 'x' | 'y' | 'z' | 'tileSize' | 'timestamp'>,
  layerId: string,
  filterJobIds: readonly string[]
): string =>
  [
    context.z,
    context.x,
    context.y,
    context.tileSize,
    context.timestamp,
    layerId,
    filterJobIds.join(','),
  ].join('|');

// Callers may append to the merged feature list, so never hand out the cached
// array itself.
const copyTileFetchResult = (result: TileFetchResult): TileFetchResult => ({
  status: result.status,
  data: result.data
    ? { ...result.data, features: [...result.data.features] }
    : null,
});

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_BASE_URL = DEFAULT_FEATURES_SERVICE_URLS.tilesApi;
const LARGE_TILE_SIZE = 512;
const DEFAULT_TILE_SIZE = 256;
const DEFAULT_SLICE_CACHE_SIZE = 4096;

const resolveIds = (
  ids: readonly string[] | (() => readonly string[]) | undefined
): readonly string[] => {
  if (!ids) {
    return [];
  }

  return typeof ids === 'function' ? ids() : ids;
};

const resolveLayerIds = (
  layerIds: FeatureTileFetcherParams['layerIds']
): readonly string[] => resolveIds(layerIds);

interface FetchVisibility {
  readonly layerIds: readonly string[];
  readonly jobIds: readonly string[] | undefined;
  readonly jobFilterEnabled: boolean;
}

const resolveFetchVisibility = (
  params: FeatureTileFetcherParams
): FetchVisibility => {
  const layerIds = resolveLayerIds(params.layerIds);
  const jobIds =
    params.jobIds !== undefined ? resolveIds(params.jobIds) : undefined;

  return {
    layerIds,
    jobIds,
    jobFilterEnabled: jobIds !== undefined,
  };
};

const shouldSkipTileFetch = (visibility: FetchVisibility): boolean =>
  visibility.layerIds.length === 0 ||
  (visibility.jobFilterEnabled && visibility.jobIds!.length === 0);

const emptyTileResult = (): TileFetchResult => ({
  data: null,
  status: HttpStatus.Ok,
});

interface LayerJobFetchTarget {
  readonly layerId: string;
  readonly filterJobIds: readonly string[];
}

const buildFetchTargets = (
  visibility: FetchVisibility
): LayerJobFetchTarget[] => {
  if (shouldSkipTileFetch(visibility)) {
    return [];
  }

  if (visibility.jobFilterEnabled) {
    return visibility.layerIds.flatMap((layerId) =>
      visibility.jobIds!.map((jobId) => ({
        layerId,
        filterJobIds: [jobId] as readonly string[],
      }))
    );
  }

  return visibility.layerIds.map((layerId) => ({
    layerId,
    filterJobIds: [] as readonly string[],
  }));
};

const isFetchTargetVisible = (
  visibility: FetchVisibility,
  target: LayerJobFetchTarget
): boolean => {
  if (shouldSkipTileFetch(visibility)) {
    return false;
  }

  if (!visibility.layerIds.includes(target.layerId)) {
    return false;
  }

  if (!visibility.jobFilterEnabled || target.filterJobIds.length === 0) {
    return true;
  }

  const [jobId] = target.filterJobIds;
  return jobId !== undefined && visibility.jobIds!.includes(jobId);
};

/**
 * Mutates each feature in `features` in place: copies `feature.metadata` and
 * writes `common_layerId` / `common_jobId` when those keys are missing.
 * Existing metadata values are left unchanged. No-op when the corresponding
 * id argument is undefined.
 */
const tagFeatureMetadata = (
  features: GeoJsonCollection['features'],
  layerId: string | undefined,
  jobId: string | undefined
): void => {
  for (const feature of features) {
    const metadata = { ...(feature.metadata ?? {}) };
    let updated = false;

    if (layerId && !metadata['common_layerId']) {
      metadata['common_layerId'] = layerId;
      updated = true;
    }

    if (jobId && !metadata['common_jobId']) {
      metadata['common_jobId'] = jobId;
      updated = true;
    }

    if (updated) {
      feature.metadata = metadata;
    }
  }
};

const hasFeatureData = (
  response: TileFetchResult
): response is TileFetchResult & { data: GeoJsonCollection } =>
  response.data !== null;

/**
 * Merged tile requests treat 204 as "this slice was empty", not as the
 * overall result. Prefer the last status that actually returned content;
 * only report 204 when every request was empty.
 */
const resolveMergedStatus = (responses: readonly TileFetchResult[]): number => {
  const lastWithContent = responses.findLast(
    (response) => response.status !== HttpStatus.NoContent
  );
  if (lastWithContent) {
    return lastWithContent.status;
  }

  return responses.length > 0 ? HttpStatus.NoContent : HttpStatus.Ok;
};

const mergeTileFetchResults = (
  responses: readonly TileFetchResult[]
): TileFetchResult => {
  const collections = responses
    .filter(hasFeatureData)
    .map((response) => response.data);
  const status = resolveMergedStatus(responses);

  if (collections.length === 0) {
    return { data: null, status };
  }

  return {
    data: {
      type: 'FeatureCollection',
      features: collections.flatMap((collection) => collection.features),
    },
    status,
  };
};

// ============================================================================
// Public API
// ============================================================================

/**
 * Creates a tile fetcher compatible with FeaturesServiceOptions.tileFetcher
 * that retrieves GeoJSON features from the Trimble Features Service API.
 *
 * `layerIds` and `jobIds` accept static arrays or getters so callers can
 * filter by current visibility. When every layer or (if `jobIds` is provided)
 * every job is hidden, the fetcher returns empty tile data without network
 * requests.
 *
 * @param params - Configuration parameters for the tile fetcher
 * @returns Async tile fetcher function that returns GeoJSON features
 *
 * @example
 * const fetcher = featureTileFetcher({
 *   projectId: 'my-project',
 *   layerIds: () => visibleLayerIds(),
 *   jobIds: () => visibleJobIds(),
 *   authToken: () => getToken(),
 *   fetchMode: 'per-layer',
 * });
 *
 * const result = await fetcher(x, y, z, abortController);
 */
export function featureTileFetcher(params: FeatureTileFetcherParams) {
  const logger = getLogger();

  // Validate and normalize tileSize
  let normalizedTileSize = params.tileSize ?? DEFAULT_TILE_SIZE;
  if (
    params.tileSize &&
    params.tileSize !== DEFAULT_TILE_SIZE &&
    params.tileSize !== LARGE_TILE_SIZE
  ) {
    logFeaturesTileDebug(
      logger,
      'warn',
      `Invalid tileSize ${params.tileSize}. Supported values are 256 and 512. Using default 256.`
    );
    normalizedTileSize = DEFAULT_TILE_SIZE;
  }

  const baseUrl = params.baseUrl ?? DEFAULT_BASE_URL;
  const tileSize =
    normalizedTileSize === LARGE_TILE_SIZE ? 'Large512' : 'Standard256';
  const fetchMode = params.fetchMode ?? 'combined';
  const loadMetricsEnabled =
    params.loadMetrics === true || featuresLoadMetrics.isEnabled();

  if (loadMetricsEnabled) {
    featuresLoadMetrics.enable();
  }

  const sliceCacheSize = params.sliceCacheSize ?? DEFAULT_SLICE_CACHE_SIZE;
  const sliceCache =
    fetchMode === 'per-layer' && sliceCacheSize > 0
      ? new LRUCache<string, TileFetchResult>({ max: sliceCacheSize })
      : undefined;
  const pendingJobs = new PendingJobTracker(params.onPendingJobIdsChange);

  return async (
    x: number,
    y: number,
    z: number,
    abortController: AbortController
  ): Promise<TileFetchResult> => {
    const visibility = resolveFetchVisibility(params);

    if (shouldSkipTileFetch(visibility)) {
      return emptyTileResult();
    }

    if (loadMetricsEnabled) {
      featuresLoadMetrics.recordVisibility(
        visibility.layerIds,
        visibility.jobIds
      );
    }

    if (abortController.signal.aborted) {
      throw new DOMException('The operation was aborted.', 'AbortError');
    }

    // Retrieve token at execution time to support token refresh
    const token = params.authToken();
    if (token === null) {
      throw new Error('Authentication token is required but was null');
    }

    const timestamp =
      (typeof params.timestamp === 'function'
        ? params.timestamp()
        : params.timestamp) ?? '';
    const jobIds = visibility.jobIds ?? [];
    const context: TileFetchContext = {
      params,
      layerIds: visibility.layerIds,
      x,
      y,
      z,
      abortController,
      baseUrl,
      tileSize,
      timestamp,
      token,
      jobIds,
      logger,
      sliceCache,
      pendingJobs,
    };

    // Parallel per layer fetching can reduce latency for large tiles with many features, but may increase load on server and client
    if (fetchMode === 'per-layer') {
      return fetchPerLayer({
        ...context,
        jobFilterEnabled: visibility.jobFilterEnabled,
      });
    }

    if (jobIds.length > 1) {
      return fetchCombinedPerJob(context);
    }

    // Combined fetching is more efficient for smaller tiles or when expecting few features, but may have higher latency for large tiles with many features
    return fetchCombined({
      ...context,
      filter: createFilterClause(visibility.layerIds, jobIds),
    });
  };
}

// ============================================================================
// Internal Fetch Strategies
// ============================================================================

/**
 * Fetches all layers in a single combined request
 * @internal
 */
async function fetchCombined(
  options: TileFetchContext & { filter: string }
): Promise<TileFetchResult> {
  if (shouldSkipTileFetch(resolveFetchVisibility(options.params))) {
    return emptyTileResult();
  }

  const {
    params,
    x,
    y,
    z,
    abortController,
    baseUrl,
    tileSize,
    filter,
    timestamp,
    token,
    jobIds,
    logger,
  } = options;
  let url = `${baseUrl}/projects/${params.projectId}/tiles/${z}/${x}/${y}?tileSize=${tileSize}&filter=${encodeURIComponent(filter)}&fields=`;

  if (timestamp) {
    url += `&timestamp=${timestamp}`;
  }

  logFeaturesTileDebug(
    logger,
    'debug',
    `[FeaturesService] request (combined) GET ${url}`,
    {
      tile: { z, x, y },
      projectId: params.projectId,
      layerIds: options.layerIds,
      jobIds,
    }
  );

  if (featuresLoadMetrics.isEnabled()) {
    featuresLoadMetrics.recordRequest(
      'combined',
      { x, y, z },
      options.layerIds.length,
      jobIds.length
    );
  }

  const response = await fetch(url, {
    signal: abortController.signal,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch tile: ${response.status} ${response.statusText}`
    );
  }

  if (response.status === HttpStatus.NoContent) {
    return { data: null, status: response.status };
  }

  const data = await response.json();
  if (!validateGeoJsonResponse(data)) {
    throw new Error('Invalid GeoJSON response: missing features array');
  }

  // When all features come from a single layer, tag them so downstream
  // consumers (e.g. per-layer styling) can resolve layer metadata.
  // For multi-layer combined responses the server doesn't return the
  // common_layerId field, so features without an existing tag remain
  // unstyled — per-layer fetch mode should be used when styling is required.
  if (options.layerIds.length === 1 || jobIds.length === 1) {
    const [layerId] =
      options.layerIds.length === 1 ? options.layerIds : [undefined];
    const [jobId] = jobIds.length === 1 ? jobIds : [undefined];
    tagFeatureMetadata(data.features, layerId, jobId);
  }

  return {
    data,
    status: response.status,
  };
}

/**
 * Fetches all layers once per visible job and merges the results. Each request
 * uses a single-job EQ filter so features can be tagged with `common_jobId` for
 * per-job visibility in the renderer.
 * @internal
 */
async function fetchCombinedPerJob(
  options: TileFetchContext
): Promise<TileFetchResult> {
  const visibility = resolveFetchVisibility(options.params);
  if (shouldSkipTileFetch(visibility)) {
    return emptyTileResult();
  }

  const jobIds = visibility.jobIds ?? [];
  const tasks = jobIds.map((jobId) => async () => {
    const currentVisibility = resolveFetchVisibility(options.params);
    if (
      shouldSkipTileFetch(currentVisibility) ||
      !currentVisibility.jobIds?.includes(jobId)
    ) {
      return emptyTileResult();
    }

    if (options.abortController.signal.aborted) {
      throw new DOMException('The operation was aborted.', 'AbortError');
    }

    return fetchCombined({
      ...options,
      layerIds: currentVisibility.layerIds,
      filter: createFilterClause(currentVisibility.layerIds, [jobId]),
      jobIds: [jobId],
    });
  });

  if (tasks.length === 0) {
    return emptyTileResult();
  }

  const responses = await promiseAllWithConcurrency(
    tasks,
    getOptimalConcurrency(jobIds.length)
  );

  return mergeTileFetchResults(responses);
}

/**
 * Fetches tiles for each layer separately in parallel and combines the results
 * @internal
 */
async function fetchPerLayer(
  options: TileFetchContext & { jobFilterEnabled: boolean }
): Promise<TileFetchResult> {
  const { abortController, logger, params } = options;

  const visibility = resolveFetchVisibility(params);
  if (shouldSkipTileFetch(visibility)) {
    return emptyTileResult();
  }

  const fetchTargets = buildFetchTargets(visibility);
  if (fetchTargets.length === 0) {
    return emptyTileResult();
  }

  // Create tasks for each layer/job fetch (throttled to prevent resource exhaustion)
  const tasks = fetchTargets.map(({ layerId, filterJobIds }) => async () => {
    const currentVisibility = resolveFetchVisibility(params);
    const target = { layerId, filterJobIds };

    if (!isFetchTargetVisible(currentVisibility, target)) {
      return emptyTileResult();
    }

    if (abortController.signal.aborted) {
      throw new DOMException('The operation was aborted.', 'AbortError');
    }

    const cacheKey = buildSliceCacheKey(options, layerId, filterJobIds);
    const cached = options.sliceCache?.get(cacheKey);
    if (cached) {
      return copyTileFetchResult(cached);
    }

    const jobId = filterJobIds.length === 1 ? filterJobIds[0] : undefined;
    options.pendingJobs.begin(jobId);
    try {
      const result = await fetchLayerSlice(options, layerId, filterJobIds);
      options.sliceCache?.set(cacheKey, result);
      return copyTileFetchResult(result);
    } finally {
      options.pendingJobs.end(jobId);
    }
  });

  // Execute with smart concurrency limit based on request count
  const concurrency = getOptimalConcurrency(tasks.length);
  logFeaturesTileDebug(
    logger,
    'debug',
    `Fetching ${fetchTargets.length} layer/job requests with concurrency: ${concurrency}`
  );

  const responses = await promiseAllWithConcurrency(tasks, concurrency);

  return mergeTileFetchResults(responses);
}

async function fetchLayerSlice(
  options: TileFetchContext,
  layerId: string,
  filterJobIds: readonly string[]
): Promise<TileFetchResult> {
  const {
    x,
    y,
    z,
    abortController,
    baseUrl,
    tileSize,
    timestamp,
    token,
    logger,
    params,
  } = options;

  const filter = createFilterClause([layerId], filterJobIds);
  let url = `${baseUrl}/projects/${params.projectId}/tiles/${z}/${x}/${y}?tileSize=${tileSize}&filter=${encodeURIComponent(filter)}&fields=`;

  if (timestamp) {
    url += `&timestamp=${timestamp}`;
  }

  logFeaturesTileDebug(
    logger,
    'debug',
    `[FeaturesService] request (per-layer) GET ${url}`,
    {
      tile: { z, x, y },
      projectId: params.projectId,
      layerId,
      jobIds: filterJobIds,
    }
  );

  if (featuresLoadMetrics.isEnabled()) {
    featuresLoadMetrics.recordRequest(
      'per-layer',
      { x, y, z },
      1,
      filterJobIds.length
    );
  }

  const response = await fetch(url, {
    signal: abortController.signal,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch tile for layer ${layerId}: ${response.status} ${response.statusText}`
    );
  }

  if (response.status === HttpStatus.NoContent) {
    return { data: null, status: response.status };
  }

  const data = await response.json();

  if (!validateGeoJsonResponse(data)) {
    throw new Error(
      `Invalid GeoJSON response for layer ${layerId}: missing features array`
    );
  }

  // Tag each feature with its source layer/job so downstream consumers
  // (e.g. per-layer styling and job visibility) can resolve metadata.
  tagFeatureMetadata(
    data.features,
    layerId,
    filterJobIds.length === 1 ? filterJobIds[0] : undefined
  );

  return { data, status: response.status };
}
